import { randomUUID as uuidv4 } from 'expo-crypto';
import { Platform } from 'react-native';

import { Stage } from 'generated/graphql-gateway';
import { appVersion } from 'utils/app-version-info';
import { LogLevel } from 'utils/datadog/types';

import { AppConfigApi } from '../../../app.config';

import { apiKeyLogger } from './api-key-logger';
import { getConfigValue } from './config';
import {
  GraphQLEnv,
  RBIAppUriScheme,
  RBIBrand,
  RBIEnv,
  RBIExpandedPlatform,
  RBIFullBrandName,
  RBIPlatform,
  Region,
} from './types';

export * from './config';

const brandToFullBrandName = {
  [RBIBrand.BK]: RBIFullBrandName.BurgerKing,
};

const brandToUriScheme = {
  [RBIBrand.BK]: RBIAppUriScheme.BurgerKing,
};

export const minDevLogLevel = () => getConfigValue({ key: 'minDevLogLevel' }) as LogLevel;
export const brand = () => getConfigValue({ key: 'brand' }) as RBIBrand;
export const env = () => (getConfigValue({ key: 'env' }) || RBIEnv.DEV) as RBIEnv;
export const platform = () => (Platform.OS === 'web' ? 'web' : 'app') as RBIPlatform;
export const checkRbiEnv = (is: RBIEnv) => is === env();
export const graphqlEnv = () => (env() as unknown) as GraphQLEnv;
export const graphqlGatewayEnv = () => (env() as unknown) as GraphQLEnv;
// TODO: RN - locale switching -- should assess if we even need this - ever a case where sanityEnv !== env?
export const sanityEnv = () => env() as RBIEnv;

export const welcomeEmailDomain = () =>
  (process.env.REACT_APP_RBI_WELCOME_EMAIL_DOMAIN || env()) as Stage;
export const fullBrandName = () => brandToFullBrandName[brand()];
export const appUriScheme = () => brandToUriScheme[brand()];
export const region = (getConfigValue({ key: 'buildRegion' }) || 'US') as Region;
export const regionEnum = region.toUpperCase() as Region;

export const isUS = region === Region.US;
export const isCA = region === Region.CA;

/** UUID of an instance of the app running that can be used to correlate logs in datadog, amplitude and crashlytics */
export const appStartId = uuidv4();

// TODO: RN - locale switching -- should pull from specific region
// Our Dataset is always of the form ENV_BRAND except in test ENV where we always use 'automation'
export const sanityDataset = () => `${sanityEnv()}_${brand()}`;

export { RBIBrand, RBIEnv, RBIPlatform, RBIExpandedPlatform, GraphQLEnv };

export const isLocalDev = process.env.NODE_ENV !== 'production';
export const isTest = process.env.NODE_ENV === 'test';
export const isProduction = checkRbiEnv(RBIEnv.PROD);
export const isWeb = Platform.OS === 'web';
export const isIos = Platform.OS === 'ios';
export const isAndroid = Platform.OS === 'android';
export const isNative = !isWeb;
export const isMobile = () => /Mobile/.test(navigator.userAgent) || isNative;
export const isIOS = () => {
  if (Platform.OS === 'ios') {
    return true;
  }
  if (Platform.OS === 'android') {
    return false;
  }

  return (
    new Set(['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod']).has(
      navigator.platform
    ) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};
export const isNativeIOS = () => isNative && isIOS();

export const isNativeAndroid = () => isNative && !isIOS();

export const getCurrentCommitHash = () => getConfigValue({ key: 'revisionId' }) || env();

export const versionIsGreaterThanOrEqualTo = ({
  versionString,
  majorVersionGreaterThanOrEqualTo,
}: {
  versionString: string | null;
  majorVersionGreaterThanOrEqualTo: number;
}): boolean => {
  if (!versionString) {
    return false;
  }
  const majorVersion = Number(versionString.split('.')[0]);
  return majorVersion >= majorVersionGreaterThanOrEqualTo;
};

export const minimumSignupAge = () => getConfigValue({ key: 'minimumSignupAge' });

export const enableNumericPhoneValidation = () =>
  getConfigValue({ key: 'enableNumericPhoneValidation' });

/** Simple function to see if we're running the app locally. */
export const isRunningLocally = () =>
  window.location.hostname === 'localhost' || !isNaN(Number(window.location?.hostname?.charAt(0)));

export type ApiKey = keyof AppConfigApi;

// export type AppConfigApiKey = keyof AppConfig
interface IGetApiKeyProps<T extends ApiKey> {
  key: T;
  defaultValue?: any;
  disallowEnvPickerValue?: boolean;
}
// Initiate an empty dictionary to prevent duplicates DD logs of missing api key
const missingApiKeys = {};

export const getApiKey = <T extends ApiKey>({
  key,
  defaultValue,
  disallowEnvPickerValue,
}: IGetApiKeyProps<T>): T extends keyof AppConfigApi ? AppConfigApi[T] : any => {
  const keys = getConfigValue({ key: 'apiKeys', disallowEnvPickerValue });

  const currentEnv = env();
  // Make sure we haven't already log the missing API keys
  if ((!keys || !keys[key]) && !missingApiKeys[String(key)]) {
    missingApiKeys[String(key)] = true;
    const error = `Missing API key in get-dynamic-app-config. API Key: ${key}, Fallback: ${
      (defaultValue as any) === '' ? 'None' : `"${defaultValue}"`
    }.`;
    const ctx = {
      appVersionCode: appVersion,
      brand: brand(),
      platform: platform(),
      stage: currentEnv,
      reason: 'front end configuration missing an API key',
    };

    apiKeyLogger(error, ctx);

    return defaultValue ?? '';
  }
  return (keys && (keys[key] as any)) ?? defaultValue;
};

export const googleServiceWebClientId = getConfigValue({ key: 'googleServiceWebClientId' });

export const appleWebClientId = getConfigValue({ key: 'appleWebClientId' });
